<template>
  <div class="fu-table" :class="`fu-table--${layout}`" data-test="fu-table" :data-layout="layout">
    <div class="fu-table__scroll">
      <div class="fu-table__inner" :style="innerStyle" role="table" :aria-label="`Follow-ups, ${total} ${noun}`">
        <div v-if="layout !== 'phone' && showHead" class="fu-table__head" :style="gridStyle" role="row">
          <div
            v-for="c in columns"
            :key="c"
            class="fu-table__th"
            :class="{ 'fu-table__th--right': def(c).right }"
            role="columnheader"
            :aria-sort="ariaSort(c)"
            :data-col="c"
          >
            <input
              v-if="c === 'check'"
              type="checkbox"
              class="fu-check"
              aria-label="Select all"
              data-test="fu-select-all"
              :checked="allChecked"
              :indeterminate.prop="someChecked"
              @change="$emit('toggle-all')"
            />
            <button
              v-else-if="def(c).sortable && def(c).label"
              type="button"
              class="fu-table__sort"
              :title="def(c).head ? def(c).label : null"
              :aria-label="def(c).head ? def(c).label : null"
              :data-test="`fu-sort-${c}`"
              @click="$emit('sort', c)"
            >
              {{ def(c).head || def(c).label }}
              <fu-icon :name="sortIcon(c)" :size="12" class="fu-table__sort-icon" />
            </button>
            <span v-else :title="def(c).head ? def(c).label : null">{{ def(c).head || def(c).label }}</span>
          </div>
        </div>

        <template v-if="busy">
          <div v-for="n in 5" :key="`sk-${n}`" class="fu-table__skeleton" data-test="fu-skeleton" aria-hidden="true">
            <span class="fu-sk fu-sk--avatar"></span>
            <span class="fu-sk fu-sk--name"></span>
            <span class="fu-sk fu-sk--mid"></span>
            <span class="fu-sk fu-sk--fill"></span>
            <span class="fu-sk fu-sk--btn"></span>
          </div>
        </template>

        <template v-else>
          <div
            v-for="(item, i) in items"
            :key="item.id || `row-${i}`"
            class="fu-table__row"
            :class="{ 'fu-table__row--selected': isSelected(item) }"
            :style="gridStyle"
            role="row"
            data-test="fu-row"
            :data-row-id="item.id"
          >
            <div
              v-for="(c, ci) in columns"
              :key="c"
              class="fu-table__td"
              :class="[`fu-table__td--${c}`, { 'fu-table__td--right': def(c).right }]"
              :style="cellStyle(ci)"
              role="cell"
              :data-col="c"
            >
              <div v-if="phoneLabel(c)" class="fu-table__label">{{ def(c).label }}</div>

              <slot
                v-if="$scopedSlots[`cell-${c}`]"
                :name="`cell-${c}`"
                :item="item"
                :opening="!!openingIds[item.id]"
                :error="!!errorIds[item.id]"
              />
              <input
                v-else-if="c === 'check'"
                type="checkbox"
                class="fu-check"
                :aria-label="`Select ${item.name || 'row'}`"
                data-test="fu-select-row"
                :checked="isSelected(item)"
                @change="$emit('toggle-row', item)"
              />
              <followups-patient-cell
                v-else-if="c === 'patient' || c === 'guarantor'"
                :item="item"
                :opening="!!openingIds[item.id]"
                :error="!!errorIds[item.id]"
                :show-ai="showAi"
                :ai-calling="!!aiCallingIds[item.id]"
                :show-list="showListBadge"
                @open="$emit('open-drawer', item)"
                @ai-call="$emit('ai-call', item)"
              />
              <followups-queue-cell
                v-else-if="c === 'queue'"
                :cell="item.cells.queue"
                :opening="!!openingIds[item.id]"
                :error="!!errorIds[item.id]"
                @open="$emit('open-drawer', item)"
              />
              <followup-cell
                v-else-if="c === 'fu'"
                :fu="item.fu"
                :today="today"
                :date-format="dateFormat"
                :assignee="item.assignee"
                :expanded="popoverRowId !== null && popoverRowId === item.id"
                @open="el => $emit('open-followup', { item, el })"
              />
              <button
                v-else-if="c === 'remove'"
                v-b-tooltip.hover.focus
                type="button"
                class="fu-table__remove"
                title="Remove from list"
                aria-label="Remove from list"
                data-test="fu-remove"
                @click="$emit('remove', item)"
              >
                <fu-icon name="trash" :size="16" />
              </button>
              <followups-data-cell v-else :col="c" :cell="item.cells[c]" :row-id="item.id" />
            </div>
          </div>

          <div v-if="!items.length" class="fu-table__empty" data-test="fu-empty">
            <slot name="empty" :filtered="filtered">
              <div class="fu-table__empty-title">{{ filtered ? 'Nothing matches' : 'Nothing to follow up' }}</div>
              <div class="fu-table__empty-sub">
                {{ filtered ? 'Try a different view or clear your filters.' : `There are no ${noun} in this list right now.` }}
              </div>
            </slot>
          </div>
        </template>
      </div>
    </div>

    <div v-if="!busy && showFoot" class="fu-table__foot">
      <span class="fu-table__count" data-test="fu-count">
        Showing <strong>{{ items.length }}</strong> of {{ total }} {{ noun }}
      </span>
      <button
        v-if="hasMore"
        type="button"
        class="fu-table__more"
        data-test="fu-show-more"
        @click="$emit('show-more')"
      >
        Show more
      </button>
    </div>
  </div>
</template>

<script>
/**
 * The follow-ups grid: sortable header, rows, loading skeleton, empty state, and the footer
 * ("Showing X of Y patients" + "Show more"). Presentational — the queue hands it the already
 * filtered, sorted and windowed `items` (rowModel.js view-models) and listens for its events.
 *
 * Columns are the queue's keys (queues.js COLUMN_DEFS). Desktop and tablet are a CSS grid on the
 * defs' widths; phone drops the header and stacks each row as a card, two fields per line, each
 * with its small uppercase label (the export's placement rules: the name, the wide text cells and
 * the follow-up take a full line).
 *
 * A host can draw a column's cell itself with a scoped slot named `cell-<column>` (receives
 * { item, opening, error }) — the Admin "Today" worklist's type chip, patient + status and
 * details. Without that slot every cell renders exactly as before.
 */
import { COLUMN_DEFS, tableMinWidth } from '@/utils/followups/queues.js';
import FuIcon from './FuIcon.vue';
import FollowupCell from './FollowupCell.vue';
import FollowupsPatientCell from './FollowupsPatientCell.vue';
import FollowupsDataCell from './FollowupsDataCell.vue';
import FollowupsQueueCell from './FollowupsQueueCell.vue';

// Cells that need a whole line in the phone card.
const PHONE_WIDE = ['ins', 'utins', 'proc', 'carrier', 'detail'];
// Cells whose phone card shows no label above them (they label themselves).
const PHONE_UNLABELLED = ['check', 'patient', 'guarantor', 'queue', 'fu', 'remove'];
// The card's heading cell — a whole line, first (Patient Profile's `queue` heads its cards).
const PHONE_HEAD = ['patient', 'guarantor', 'queue'];

export default {
  name: 'FollowupsTable',
  components: { FuIcon, FollowupCell, FollowupsPatientCell, FollowupsDataCell, FollowupsQueueCell },
  props: {
    columns: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    layout: { type: String, default: 'desktop' },
    sort: { type: Object, default: null },            // { key, dir } | null
    selectable: { type: Boolean, default: false },
    selectedIds: { type: Object, default: () => ({}) },
    allChecked: { type: Boolean, default: false },
    someChecked: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    noun: { type: String, default: 'patients' },
    hasMore: { type: Boolean, default: false },
    filtered: { type: Boolean, default: false },       // rows exist but none match → "Nothing matches"
    today: { type: String, required: true },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    openingIds: { type: Object, default: () => ({}) },
    errorIds: { type: Object, default: () => ({}) },
    aiCallingIds: { type: Object, default: () => ({}) },
    showAi: { type: Boolean, default: false },
    showListBadge: { type: Boolean, default: true },
    popoverRowId: { type: String, default: null },
    // "Showing X of Y" + Show more. Patient Profile's handful of rows leaves it out.
    showFoot: { type: Boolean, default: true },
    // The column header row. The Admin "Today" worklist (A1) draws ONE header over several
    // sections, each a table without its own. Default = every existing caller's header.
    showHead: { type: Boolean, default: true },
  },

  computed: {
    hasCheck() {
      return this.columns[0] === 'check';
    },
    hasRemove() {
      return this.columns.indexOf('remove') !== -1;
    },
    gridTemplate() {
      if (this.layout === 'phone') {
        return this.hasCheck ? '24px minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)';
      }
      return this.columns.map(c => this.def(c).width).join(' ');
    },
    gridStyle() {
      return { gridTemplateColumns: this.gridTemplate };
    },
    // Below the columns' combined minimum the card scrolls sideways instead of squeezing them
    // (queues.js columnsFor already drops desktop columns to fit, so this is the last resort).
    innerStyle() {
      if (this.layout === 'phone') return {};
      return { minWidth: `${tableMinWidth(this.columns)}px` };
    },
    // grid-column per column index on phone (the export's `cp` rules).
    phonePlacement() {
      const full = this.hasCheck ? '2 / -1' : '1 / -1';
      let ppos = 0;
      return this.columns.map((c) => {
        if (c === 'check') return '1';
        if (PHONE_HEAD.indexOf(c) !== -1) { ppos = 0; return full; }
        if (c === 'fu') {
          if (!this.hasRemove) return full;
          return this.hasCheck ? '2 / 3' : '1 / 2';
        }
        if (c === 'remove') return this.hasCheck ? '3' : '2';
        if (PHONE_WIDE.indexOf(c) !== -1) { ppos = 0; return full; }
        const col = (this.hasCheck ? 2 : 1) + (ppos % 2);
        ppos += 1;
        return String(col);
      });
    },
  },

  methods: {
    def(c) {
      return COLUMN_DEFS[c] || { label: '', width: 'minmax(0,1fr)', sortable: false };
    },
    isSelected(item) {
      return !!(item.id && this.selectedIds[item.id]);
    },
    cellStyle(ci) {
      if (this.layout !== 'phone') return null;
      const c = this.columns[ci];
      return {
        gridColumn: this.phonePlacement[ci],
        justifySelf: c === 'remove' ? 'end' : (c === 'check' || c === 'fu' ? 'start' : 'stretch'),
      };
    },
    phoneLabel(c) {
      return this.layout === 'phone' && PHONE_UNLABELLED.indexOf(c) === -1 && !!this.def(c).label;
    },
    sortIcon(c) {
      if (!this.sort || this.sort.key !== c) return 'sort';
      return this.sort.dir === -1 ? 'down' : 'up';
    },
    ariaSort(c) {
      if (!this.def(c).sortable) return null;
      if (!this.sort || this.sort.key !== c) return 'none';
      return this.sort.dir === -1 ? 'descending' : 'ascending';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-table__scroll {
  overflow-x: auto;
}

.fu-table__head {
  display: grid;
  gap: $fu-space-3;
  align-items: center;
  padding: 11px 18px;
  background: $fu-neutral-25;
  border-bottom: 1px solid $fu-border;
  @include fu-eyebrow;
}

// Headers never wrap (polish 2026-10-03: "Recall due" / "Last hygienist" went to two lines at
// 1280); queues.js sizes each column's minimum to fit its header and sort icon.
.fu-table__th {
  min-width: 0;
  white-space: nowrap;
}

.fu-table__th--right {
  display: flex;
  justify-content: flex-end;
}

.fu-table__sort {
  @include fu-button-reset;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: $fu-radius-xs;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  user-select: none;
  &:hover { color: $fu-text; }
  @include fu-focus-ring;
}

.fu-table__sort-icon {
  opacity: 0.7;
}

.fu-check {
  @include fu-checkbox;
}

.fu-table__row {
  display: grid;
  gap: $fu-space-3;
  align-items: center;
  padding: $fu-space-3 18px;
  border-bottom: 1px solid $fu-border;
  &:hover { box-shadow: inset 0 0 0 100vmax rgba(12, 15, 14, 0.022); }
}

.fu-table__row--selected {
  background: $fu-brand-50;
}

.fu-table__td {
  min-width: 0;
}

.fu-table__td--right {
  text-align: right;
}

.fu-table__label {
  @include fu-eyebrow($fu-text-subtle, 10.5px);
  margin-bottom: 3px;
}

.fu-table__td--remove {
  display: flex;
  justify-content: flex-end;
}

.fu-table__remove {
  @include fu-icon-btn(32px);
}

.fu-table__skeleton {
  display: flex;
  align-items: center;
  gap: $fu-space-4;
  padding: $fu-space-4 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-sk--avatar { @include fu-skeleton(34px, 34px, 50%); flex: none; }
.fu-sk--name { @include fu-skeleton(180px, 12px); }
.fu-sk--mid { @include fu-skeleton(120px, 12px); }
.fu-sk--fill { @include fu-skeleton(auto, 12px); flex: 1; background: $fu-neutral-50; }
.fu-sk--btn { @include fu-skeleton(150px, 30px, 9px); flex: none; }

.fu-table__empty {
  padding: 56px 20px;
  text-align: center;
}

.fu-table__empty-title {
  font-weight: $fu-fw-bold;
  color: $fu-neutral-600;
  font-size: 15px;
}

.fu-table__empty-sub {
  font-size: 13.5px;
  margin-top: 4px;
  color: $fu-text-subtle;
}

.fu-table__foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px $fu-space-4;
  padding: 13px 18px;
  font-size: 13px;
  color: $fu-text-muted;
  strong { color: $fu-text; }
}

.fu-table__more {
  @include fu-btn(secondary, sm);
}

// ---- phone: cards -----------------------------------------------------------------------------
.fu-table--phone {
  .fu-table__scroll { overflow-x: hidden; }
  .fu-table__row {
    gap: 10px $fu-space-3;
    align-items: start;
    padding: 14px $fu-space-4;
  }
  .fu-table__td--right { text-align: left; }
  .fu-table__skeleton { padding: 14px $fu-space-4; }
  .fu-sk--name { width: 120px; }
  .fu-sk--mid,
  .fu-sk--btn { display: none; }
  .fu-table__foot { padding: 13px $fu-space-4; }
}
</style>
